import { http } from './http'
import { auth } from './auth'
import {
  ActionItemSchema,
  AgendaColumnSchema,
  AgendaNoteSchema,
  AgendaObjectServerSchema,
  ApprovalGroupSchema,
  ApproverSchema,
  BlacklineSchema,
  CategorySchema,
  ClosingBookDocumentSetSchema,
  ClosingBookEventSchema,
  ClosingVersionSchema,
  DealVariableSchema,
  DetectedSignatureSchema,
  EntitySchema,
  FileMatchSchema,
  IndividualSchema,
  ItemApprovalSchema,
  ItemSchema,
  ItemVariableSchema,
  ItemWithVariableSchema,
  MarkupSchema,
  RenderedVersionSchema,
  SignatureSchema,
  SignatureMatchSchema,
  SignaturePageSchema,
  SigningPositionSchema,
  SnapshotSchema,
  ScheduleSchema,
  SubscribeToPusherSchema,
  UploadSchema,
  VersionDetectedVariableSchema,
  VersionRenderedVariableSchema,
  VersionSchema,
} from '../schemas'
import { ClosingBookResponseSchema } from './closing-books'
import { SnapshotResponseSchema } from './snapshots'
import { z } from 'zod'
import { AgendaObjectListSchema, AgendaColumnListSchema } from './agenda'
import {
  ActionItemsResponseSchema,
  CategoriesResponseSchema,
  DealInviteResponseSchema,
  DealUsershipsResponseSchema,
  ItemsResponseSchema,
} from './deals'
import { serverVariables } from '../lib'
import { parseWithZod } from '../utils/parse-with-zod'
import { batchArray } from '../utils/batch-array'
import * as Sentry from '@sentry/react'
import { FirmResponseSchema } from './firms'

const ActionItemResponseSchema = z.object({ action_item: ActionItemSchema })

const AgendaObjectResponseSchema = z.object({ agenda_object: AgendaObjectServerSchema })
const AgendaColumnsResponseSchema = z.object({ agenda_column: AgendaColumnSchema })

const ApprovalGroupResponseSchema = z.object({ approval_group: ApprovalGroupSchema })
const ApprovalGroupsListResponseSchema = z.object({
  approval_groups: z.array(ApprovalGroupSchema),
})

const ApproverResponseSchema = z.object({ approver: ApproverSchema })
const ApproversListResponseSchema = z.object({ approvers: z.array(ApproverSchema) })

const ItemApprovalResponseSchema = z.object({ item_approval: ItemApprovalSchema })
const ItemApprovalsListResponseSchema = z.object({
  item_approvals: z.array(ItemApprovalSchema),
})

const CategoryResponseSchema = z.object({ category: CategorySchema })
const BlacklineResponseSchema = z.object({ blackline: BlacklineSchema })

const ClosingBookEventResponseSchema = z.object({
  closing_book_event: ClosingBookEventSchema,
})

const ClosingBookDocumentResponseSchema = z.object({
  closing_book_document_set: ClosingBookDocumentSetSchema,
})

const ClosingVersionResponseSchema = z.object({
  closing_version: ClosingVersionSchema.omit({ document: true }).extend({
    document: z
      .object({
        filename: z.string(),
        file_size: z.number(),
        extension: z.string(),
      })
      .nullable(),
  }),
})

const ItemResponseSchema = z.object({ item: ItemSchema })
const VersionResponseSchema = z.object({ version: VersionSchema })
const VersionListResponseSchema = z.object({ versions: z.array(VersionSchema) })
const SnapshotListResponseSchema = z.object({ snapshots: z.array(SnapshotSchema) })
const MarkupListResponseSchema = z.object({ markups: z.array(MarkupSchema) })
const MarkupResponseSchema = z.object({ markup: MarkupSchema })

const ScheduleResponseSchema = z.object({ schedule: ScheduleSchema })
const ScheduleListResponseSchema = z.object({ schedules: z.array(ScheduleSchema) })

const UploadSingleResponseSchema = z.object({ upload: UploadSchema })

const DealVariableResponseSchema = z.object({ deal_variable: DealVariableSchema })
const DealVariableListResponseSchema = z.object({ deal_variables: z.array(DealVariableSchema) })
const FileMatchesResponseSchema = z.object({ file_matches: z.array(FileMatchSchema) })
const SignatureMatchesResponseSchema = z.object({
  signature_matches: z.array(SignatureMatchSchema),
})
const SignaturePagesResponseSchema = z.object({
  signature_pages: z.array(SignaturePageSchema),
})
const SignaturesResponseSchema = z.object({ signatures: z.array(SignatureSchema) })
const DetectedSignaturesResponseSchema = z.object({
  detected_signatures: z.array(DetectedSignatureSchema),
})
const IndividualsResponseSchema = z.object({ individuals: z.array(IndividualSchema) })
const EntitiesResponseSchema = z.object({ entities: z.array(EntitySchema) })
const SigningPositionsResponseSchema = z.object({
  signing_positions: z.array(SigningPositionSchema),
})
const ItemVariableResponseSchema = z.object({ item_variable: ItemVariableSchema })
const RenderedVersionResponseSchema = z.object({ rendered_version: RenderedVersionSchema })
const VersionDetectedVariablesResponseSchema = z.object({
  version_detected_variables: z.array(VersionDetectedVariableSchema),
})
const VersionRenderedVariableResponseSchema = z.object({
  version_rendered_variables: z.array(VersionRenderedVariableSchema),
})

export const pusher = {
  subscribe: async (dealId: number, payload: Record<string, unknown>) => {
    const regionHost = auth.getUserRegionHost()
    const response = await http.post(
      `${regionHost}/api/deals/${dealId}/subscribe_to_pusher`,
      payload,
    )
    return parseWithZod(SubscribeToPusherSchema, response)
  },
  'action-item': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/action_items/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(ActionItemResponseSchema, result)
      return [parsedResult.action_item]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/action_items?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(ActionItemsResponseSchema, result).action_items
      })
    }
  },
  'agenda-object': async (agendaId: number, ids: number[], dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/agenda_objects/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(AgendaObjectResponseSchema, result)
      return [parsedResult.agenda_object]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/agendas/${agendaId}/agenda_objects?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(AgendaObjectListSchema, result).agenda_objects
      })
    }
  },
  'agenda-column': async (agendaId: number, ids: number[], dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/agenda_columns/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(AgendaColumnsResponseSchema, result)
      return [parsedResult.agenda_column]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/agendas/${agendaId}/agenda_columns?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(AgendaColumnListSchema, result).agenda_columns
      })
    }
  },
  'approval-group': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(
        `${regionHost}/api/approval_groups/${ids[0]}`,
        undefined,
        dealId,
      )
      const parsedResult = parseWithZod(ApprovalGroupResponseSchema, result)
      return [parsedResult.approval_group]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/approval_groups?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(ApprovalGroupsListResponseSchema, result).approval_groups
      })
    }
  },
  approver: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/approvers/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(ApproverResponseSchema, result)
      return [parsedResult.approver]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/approvers?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(ApproversListResponseSchema, result).approvers
      })
    }
  },
  category: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/categories/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(CategoryResponseSchema, result)
      return [parsedResult.category]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/categories?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(CategoriesResponseSchema, result).categories
      })
    }
  },
  blackline: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return Promise.all(
      ids.map(async id => {
        const result = await http.get(`${regionHost}/api/blacklines/${id}`, undefined, dealId)
        return parseWithZod(BlacklineResponseSchema, result).blackline
      }),
    )
  },
  'closing-book': async (id: number, dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/closing_books/${id}`, undefined, dealId)
    const parsedResult = parseWithZod(ClosingBookResponseSchema, result)
    return parsedResult.closing_book
  },
  'closing-book-event': async (eventId: number, dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/closing_book_events/${eventId}`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(ClosingBookEventResponseSchema, result)
    return parsedResult.closing_book_event
  },
  'closing-book-document-set': async (documentSetId: number, dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/closing_book_document_sets/${documentSetId}`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(ClosingBookDocumentResponseSchema, result)
    return parsedResult.closing_book_document_set
  },
  'closing-version': async (closingVersionId: number, dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/closing_versions/${closingVersionId}`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(ClosingVersionResponseSchema, result)
    return parsedResult.closing_version
  },
  'deal-usership': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/userships?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(DealUsershipsResponseSchema, result).deal_userships
    })
  },
  'deal-invite': async (inviteId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deal_invites/${inviteId}`,
      undefined,
      serverVariables.dealId ?? undefined,
    )
    const parsedResult = parseWithZod(DealInviteResponseSchema, result)
    return parsedResult.deal_invite
  },
  'file-match': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/file_matches?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(FileMatchesResponseSchema, result).file_matches
    })
  },
  'signature-match': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(
        `${regionHost}/api/signature_matches/${ids[0]}`,
        undefined,
        dealId,
      )
      const parsedResult = parseWithZod(z.object({ signature_match: SignatureMatchSchema }), result)
      return [parsedResult.signature_match]
    }

    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/signature_matches?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(SignatureMatchesResponseSchema, result).signature_matches
    })
  },
  'signature-page': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/signature_pages?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(SignaturePagesResponseSchema, result).signature_pages
    })
  },
  signature: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/signatures?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(SignaturesResponseSchema, result).signatures
    })
  },
  'detected-signature': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/detected_signatures?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(DetectedSignaturesResponseSchema, result).detected_signatures
    })
  },
  individual: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/individuals?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(IndividualsResponseSchema, result).individuals
    })
  },
  entity: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/entities?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(EntitiesResponseSchema, result).entities
    })
  },
  'signing-position': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/signing_positions?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      return parseWithZod(SigningPositionsResponseSchema, result).signing_positions
    })
  },
  firm: async (firmId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/firms/${firmId}`, undefined, dealId)
    const parsedResult = parseWithZod(FirmResponseSchema, result)
    return parsedResult.firm
  },
  item: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/items/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(ItemResponseSchema, result)
      return [parsedResult.item]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/items?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(ItemsResponseSchema, result).items
      })
    }
  },
  'item-approval': async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/item_approvals/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(ItemApprovalResponseSchema, result)
      return [parsedResult.item_approval]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/item_approvals?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(ItemApprovalsListResponseSchema, result).item_approvals
      })
    }
  },
  version: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/versions/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(VersionResponseSchema, result)
      return [parsedResult.version]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/versions?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(VersionListResponseSchema, result).versions
      })
    }
  },
  markup: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/markups/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(MarkupResponseSchema, result)
      return [parsedResult.markup]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/markups?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(MarkupListResponseSchema, result).markups
      })
    }
  },
  schedule: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/schedules/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(ScheduleResponseSchema, result)
      return [parsedResult.schedule]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/schedules?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(ScheduleListResponseSchema, result).schedules
      })
    }
  },
  snapshot: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/snapshots/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(SnapshotResponseSchema, result)
      return [parsedResult.snapshot]
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/snapshots?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        return parseWithZod(SnapshotListResponseSchema, result).snapshots
      })
    }
  },
  upload: async (uploadId: number, dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/uploads/${uploadId}`, undefined, dealId)
    const parsedResult = parseWithZod(UploadSingleResponseSchema, result)
    return parsedResult.upload
  },
  'deal-variable': async (ids: number[], dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/deal_variables/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(DealVariableResponseSchema, result)
      return parsedResult.deal_variable
    } else {
      return fetchInBatches(ids, async batch => {
        const result = await http.get(
          `${regionHost}/api/deals/${dealId}/deal_variables?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        const parsedResult = parseWithZod(DealVariableListResponseSchema, result)
        return parsedResult.deal_variables
      })
    }
  },
  'item-variable': async (ids: number[], dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(`${regionHost}/api/item_variables/${ids[0]}`, undefined, dealId)
      const parsedResult = parseWithZod(ItemVariableResponseSchema, result)
      return parsedResult.item_variable
    } else {
      const itemVariables = await Promise.all(
        ids.map(async id => {
          const result = await http.get(`${regionHost}/api/item_variables/${id}`, undefined, dealId)
          const parsedResult = parseWithZod(ItemVariableResponseSchema, result)
          return parsedResult.item_variable
        }),
      )
      return itemVariables
    }
  },
  'rendered-version': async (ids: number[], dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    if (ids.length === 1) {
      const result = await http.get(
        `${regionHost}/api/rendered_versions/${ids[0]}`,
        undefined,
        dealId,
      )
      const parsedResult = parseWithZod(RenderedVersionResponseSchema, result)
      return parsedResult.rendered_version
    } else {
      const renderedVersions = await Promise.all(
        ids.map(async id => {
          const result = await http.get(
            `${regionHost}/api/rendered_versions/${id}`,
            undefined,
            dealId,
          )
          const parsedResult = parseWithZod(RenderedVersionResponseSchema, result)
          return parsedResult.rendered_version
        }),
      )
      return renderedVersions
    }
  },
  'version-detected-variable': async (ids: number[], dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/version_detected_variables?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      const parsedResult = parseWithZod(VersionDetectedVariablesResponseSchema, result)
      return parsedResult.version_detected_variables
    })
  },
  'version-rendered-variable': async (ids: number[], dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/version_rendered_variables?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      const parsedResult = parseWithZod(VersionRenderedVariableResponseSchema, result)
      return parsedResult.version_rendered_variables
    })
  },
  'item-with-variable': async (ids: number[], dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return fetchInBatches(ids, async batch => {
      const result = await http.get(
        `${regionHost}/api/deals/${dealId}/item_with_variables?${buildIdsQueryString(batch)}`,
        undefined,
        dealId,
      )
      const parsedResult = parseWithZod(
        z.object({ item_with_variables: z.array(ItemWithVariableSchema) }),
        result,
      )
      return parsedResult.item_with_variables
    })
  },
  'agenda-note': async (noteIds: number[], dealId?: number) => {
    const regionHost = auth.getUserRegionHost()
    if (noteIds.length === 1) {
      const result = await http.get(
        `${regionHost}/api/agenda_notes/${noteIds[0]}`,
        undefined,
        dealId,
      )
      const parsedResult = parseWithZod(z.object({ agenda_note: AgendaNoteSchema }), result)
      return [parsedResult.agenda_note]
    } else {
      return fetchInBatches(noteIds, async batch => {
        const result = await http.get(
          `${regionHost}/api/agenda_notes?${buildIdsQueryString(batch)}`,
          undefined,
          dealId,
        )
        const parsedResult = parseWithZod(
          z.object({ agenda_notes: z.array(AgendaNoteSchema) }),
          result,
        )
        return parsedResult.agenda_notes
      })
    }
  },
}

function buildIdsQueryString(ids: number[]) {
  const query = new URLSearchParams()
  ids.forEach(id => query.append('ids[]', id.toString()))
  return query.toString()
}

const IDS_PER_REQUEST = 100

/**
 * Fans a large id list out over concurrent requests, since the API caps how many `ids[]` one call
 * may carry.
 *
 * Batches that fail are retried once, then reported to Sentry and dropped — so this resolves with a
 * partial list rather than rejecting, and callers cannot tell. That suits live sync, where the next
 * push corrects a gap, but not anywhere a complete list is required.
 */
async function fetchInBatches<T>(
  ids: number[],
  fetchBatch: (batch: number[]) => Promise<T[]>,
): Promise<T[]> {
  if (ids.length <= IDS_PER_REQUEST) return fetchBatch(ids)

  const fetchBatchWithRetry = async (batch: number[]) => {
    try {
      return await fetchBatch(batch)
    } catch {
      return fetchBatch(batch)
    }
  }

  const batches = batchArray(ids, IDS_PER_REQUEST)
  const results = await Promise.allSettled(batches.map(fetchBatchWithRetry))

  results.forEach(result => {
    if (result.status !== 'rejected') return
    console.error('Batched pusher request failed:', result.reason)
    Sentry.captureException(result.reason)
  })
  return results
    .filter((result): result is PromiseFulfilledResult<T[]> => result.status === 'fulfilled')
    .flatMap(result => result.value)
}
